import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchAndFilter from "../../../component/SearchAndFilter/SearchAndFilter";
import { filters } from "../../../utils/data";
import { useParams } from "react-router-dom";
import {
  setInvestorLiked,
  setInvestorRequestConnection,
} from "../../../store/investorsWithFirms";
import { Badge, getFundingBadgeVariant } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Avatar } from "../../../components/ui/avatar";
import { Card } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { Heart, Zap, ChevronLeft, ChevronRight, ExternalLink, MapPin, Briefcase, Mail, Building2, TrendingUp, Check, X, Users, Globe, Linkedin } from "lucide-react";

// Top-level tabs scope the directory by contact type. "Others" holds any
// contact that is not an Investor / Debt / Banker, with a sub-category filter.
const TABS = [
  { key: "investor", label: "Investors", type: "Investor" },
  { key: "debt", label: "Debt", type: "Debt" },
  { key: "banker", label: "Bankers", type: "Banker" },
  { key: "others", label: "Others", type: "Others" },
];
const KNOWN_TYPES = ["Investor", "Debt", "Banker"];
const OTHERS_SUBCATEGORIES = ["Advisors", "Legal", "Others"];
const LEVELS = ["L1", "L2", "L3"];

const resolveTabKey = (param) => {
  if (!param) return "investor";
  const p = param.toLowerCase();
  const direct = TABS.find((t) => t.key === p);
  if (direct) return direct.key;
  // Backward compatibility for legacy /dashboard/type/:type deep links
  // (e.g. "investors", "bankers", "Investor").
  const byType = TABS.find(
    (t) => t.type.toLowerCase() === p || `${t.type.toLowerCase()}s` === p
  );
  return byType ? byType.key : "investor";
};

const styles = {
  investorCard: {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#f1f5f9',
    padding: '1rem',
    transition: 'all 0.2s ease',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  avatarWrapper: {
    position: 'relative',
  },
  likeButton: {
    position: 'absolute',
    bottom: '-4px',
    right: '-4px',
    padding: '0.375rem',
    borderRadius: '50%',
    transition: 'all 0.2s ease',
    border: 'none',
    cursor: 'pointer',
  },
  likeButtonActive: {
    backgroundColor: '#f43f5e',
    color: 'white',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  likeButtonInactive: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    color: '#94a3b8',
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: 600,
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '1rem',
    margin: 0,
  },
  designation: {
    fontSize: '0.875rem',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    margin: '0.25rem 0 0 0',
  },
  location: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    margin: '0.125rem 0 0 0',
  },
  badgesWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375rem',
    marginTop: '0.75rem',
  },
  cardFooter: {
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsButton: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: 500,
    transition: 'all 0.15s ease',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: '0.375rem 0.75rem',
    borderRadius: '0.375rem',
  },
  detailsButtonHover: {
    backgroundColor: 'rgba(222,194,170,0.2)',
    color: '#1a1a1a',
  },
  chip: {
    color: '#475569',
    backgroundColor: '#f8fafc',
    padding: '0.125rem 0.5rem',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
  },
  sectorChip: {
    color: '#1a1a1a',
    backgroundColor: 'rgba(222,194,170,0.3)',
    padding: '0.125rem 0.5rem',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
  },
  firmHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    background: 'linear-gradient(to right, #f8fafc, white)',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  firmLogo: {
    width: '3rem',
    height: '3rem',
    borderRadius: '0.5rem',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  firmName: {
    fontWeight: 600,
    fontSize: '1.125rem',
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  firmCount: {
    fontSize: '0.875rem',
    color: '#64748b',
    margin: 0,
  },
  investorsGrid: {
    padding: '1rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1rem',
  },
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#fafafa',
    paddingTop: '4rem',
  },
  stickyHeader: {
    position: 'sticky',
    top: '4rem',
    zIndex: 40,
    backgroundColor: '#fafafa',
    paddingBottom: '1rem',
    marginTop: '0',
  },
  tabBarWrapper: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: '0.75rem 1rem 0 1rem',
  },
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flexWrap: 'wrap',
    borderBottom: '2px solid #e2e8f0',
  },
  tab: {
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#64748b',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    color: '#1a1a1a',
    fontWeight: 600,
    borderBottom: '2px solid #DEC2AA',
  },
  header: {
    background: '#1a1a1a',
    color: 'white',
    padding: '0.5rem 1.5rem',
    position: 'relative',
    overflow: 'hidden',
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 80%, rgba(222,194,170,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(190,210,245,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  headerContent: {
    maxWidth: '80rem',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  headerSubtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.875rem',
    margin: 0,
  },
  mainContent: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: '1rem 1rem 1.5rem 1rem',
  },
  filterWrapper: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: '1rem 1rem 0 1rem',
  },
  filterSection: {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    padding: '1rem 1.5rem',
    position: 'relative',
    zIndex: 10,
  },
  filterInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  paginationWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'rgba(222,194,170,0.2)',
    borderRadius: '0.5rem',
    padding: '0.25rem',
    border: '1px solid rgba(222,194,170,0.4)',
  },
  paginationButton: {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    color: '#1a1a1a',
  },
  paginationText: {
    padding: '0 0.75rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a1a1a',
    minWidth: '80px',
    textAlign: 'center',
  },
  firmsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
  },
  emptyIcon: {
    color: '#94a3b8',
    marginBottom: '1rem',
  },
  emptyTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
  },
  emptyText: {
    color: '#64748b',
    maxWidth: '24rem',
    margin: '0 auto',
  },
  bottomPagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2rem',
    marginBottom: '1rem',
  },
  bottomPaginationInner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    padding: '0.75rem',
  },
  bottomPaginationText: {
    padding: '0 1rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a1a1a',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '1rem',
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '1rem',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    position: 'relative',
  },
  modalHeader: {
    background: '#1a1a1a',
    padding: '2rem',
    position: 'relative',
    borderRadius: '1rem 1rem 0 0',
  },
  modalCloseButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    padding: '0.5rem',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  modalProfileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
  },
  modalAvatar: {
    width: '5rem',
    height: '5rem',
    borderRadius: '50%',
    border: '4px solid rgba(107,114,128,0.3)',
    backgroundColor: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    fontWeight: 600,
    color: '#ffffff',
    overflow: 'hidden',
  },
  modalName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'white',
    margin: 0,
  },
  modalDesignation: {
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.9)',
    margin: '0.25rem 0 0 0',
  },
  modalFirm: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.7)',
    margin: '0.25rem 0 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  modalBody: {
    padding: '1.5rem 2rem',
  },
  modalSection: {
    marginBottom: '1.5rem',
  },
  modalSectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
  },
  modalInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.5rem',
  },
  modalBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  modalFooter: {
    padding: '1.5rem 2rem',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
  },
};

const InvestorModal = memo(({ investor, firmName, isOpen, onClose, isLiked, isConnectionRequested, onLike, onRequestConnection }) => {
  if (!isOpen || !investor) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <button 
            style={styles.modalCloseButton} 
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          
          <div style={styles.modalProfileSection}>
            <Avatar 
              src={investor.photo} 
              alt={investor.name}
              fallback={investor.name}
              style={{ 
                width: '5rem', 
                height: '5rem', 
                fontSize: '2rem',
                border: '4px solid rgba(229,231,235,0.5)',
              }}
            />
            <div>
              <h2 style={styles.modalName}>{investor.name || '-'}</h2>
              <p style={styles.modalDesignation}>
                {investor.designation || '-'}
                {investor.level ? (
                  <span style={{
                    display: 'inline-block',
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: '#DEC2AA',
                    color: '#1a1a1a',
                  }}>
                    {investor.level}
                  </span>
                ) : null}
              </p>
              <p style={styles.modalFirm}>
                <Building2 style={{ width: '0.875rem', height: '0.875rem' }} />
                {firmName || '-'}
              </p>
            </div>
          </div>
        </div>

        <div style={styles.modalBody}>
          {investor.email && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Contact</div>
              <div style={styles.modalInfoRow}>
                <Mail style={{ width: '1.25rem', height: '1.25rem', color: '#1a1a1a' }} />
                <span style={{ color: '#334155' }}>{investor.email}</span>
              </div>
              {investor.linkedIn && (
                <div 
                  style={{ ...styles.modalInfoRow, cursor: 'pointer' }}
                  onClick={() => {
                    let url = investor.linkedIn;
                    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                      url = 'https://' + url;
                    }
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <Linkedin style={{ width: '1.25rem', height: '1.25rem', color: '#0077b5' }} />
                  <span style={{ color: '#0077b5', textDecoration: 'underline' }}>View LinkedIn Profile</span>
                </div>
              )}
            </div>
          )}

          {(investor.locationCountries || investor.locationCities?.length > 0) && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Location</div>
              <div style={styles.modalInfoRow}>
                <Globe style={{ width: '1.25rem', height: '1.25rem', color: '#1a1a1a' }} />
                <span style={{ color: '#334155' }}>
                  {investor.locationCities?.join(', ')} {investor.locationCountries && `(${investor.locationCountries})`}
                </span>
              </div>
            </div>
          )}

          {investor.stage?.length > 0 && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Funding Stages</div>
              <div style={styles.modalBadges}>
                {investor.stage.map((el, idx) => (
                  <Badge key={idx} variant={getFundingBadgeVariant(el)}>
                    {el}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {investor.sector?.length > 0 && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Sectors</div>
              <div style={styles.modalBadges}>
                {investor.sector.map((s, idx) => (
                  <span key={idx} style={{ ...styles.sectorChip, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {investor.pastInvestments?.length > 0 && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Past Investments</div>
              <div style={styles.modalBadges}>
                {investor.pastInvestments.map((inv, idx) => (
                  <span key={idx} style={{ ...styles.chip, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}>
                    {inv}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <Button
            variant={isLiked ? "destructive" : "outline"}
            onClick={onLike}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Heart style={{ width: '1rem', height: '1rem', fill: isLiked ? 'currentColor' : 'none' }} />
            {isLiked ? 'Liked' : 'Like'}
          </Button>
          <Button
            variant={isConnectionRequested ? "success" : "default"}
            onClick={onRequestConnection}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {isConnectionRequested ? (
              <>
                <Check style={{ width: '1rem', height: '1rem' }} />
                Connected
              </>
            ) : (
              <>
                <Zap style={{ width: '1rem', height: '1rem' }} />
                Request Connection
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
});

const InvestorCard = memo(({ 
  people, 
  firmName,
  isLiked, 
  isConnectionRequested, 
  onLike, 
  onRequestConnection,
  onViewDetails
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDetailsHovered, setIsDetailsHovered] = useState(false);
  const status = (people.status || 'active').toLowerCase();
  const statusStyles = status === 'inactive'
    ? { backgroundColor: '#FEF2F2', borderLeft: '3px solid #ef4444' }
    : status === 'follower'
    ? { backgroundColor: '#FFFEF5', borderLeft: '3px solid #eab308' }
    : {};
  
  return (
    <div 
      style={{
        ...styles.investorCard,
        ...statusStyles,
        boxShadow: isHovered ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none',
        borderColor: isHovered ? '#e2e8f0' : (status !== 'active' ? undefined : '#f1f5f9'),
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.cardHeader}>
        <div style={styles.avatarWrapper}>
          <Avatar 
            src={people.photo} 
            alt={people.name} 
            fallback={people.name}
            size="xl"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLike();
            }}
            style={{
              ...styles.likeButton,
              ...(isLiked ? styles.likeButtonActive : styles.likeButtonInactive),
            }}
          >
            <Heart style={{ width: '0.875rem', height: '0.875rem', fill: isLiked ? 'currentColor' : 'none' }} />
          </button>
        </div>
        
        <div style={styles.cardContent}>
          <h3 style={styles.name}>
            {people?.name || "-"}
            {people?.linkedIn && (
              <a
                href={people.linkedIn.startsWith('http') ? people.linkedIn : `https://${people.linkedIn}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ marginLeft: '6px', color: '#0077b5', display: 'inline-flex', verticalAlign: 'middle' }}
              >
                <Linkedin style={{ width: '0.875rem', height: '0.875rem' }} />
              </a>
            )}
            {status !== 'active' && (
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                display: 'inline-block',
                marginLeft: '6px',
                verticalAlign: 'middle',
                backgroundColor: status === 'inactive' ? '#ef4444' : '#eab308',
              }} />
            )}
          </h3>
          <p style={styles.designation}>
            <Briefcase style={{ width: '0.875rem', height: '0.875rem' }} />
            {people?.designation || "-"}
            {people?.level ? (
              <span style={{
                display: 'inline-block',
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: '#DEC2AA',
                color: '#1a1a1a',
              }}>
                {people.level}
              </span>
            ) : null}
          </p>
          <p style={styles.location}>
            <MapPin style={{ width: '0.875rem', height: '0.875rem' }} />
            {people?.locationCountries || "-"}
          </p>
          
          <div style={styles.badgesWrapper}>
            {people?.stage?.slice(0, 3).map((el, idx) => (
              <Badge key={idx} variant={getFundingBadgeVariant(el)}>
                {el}
              </Badge>
            ))}
            {people?.stage?.length > 3 && (
              <Badge variant="secondary">
                +{people.stage.length - 3}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      <div style={styles.cardFooter}>
        <button
          onClick={onViewDetails}
          style={{
            ...styles.detailsButton,
            ...(isDetailsHovered ? styles.detailsButtonHover : {}),
          }}
          onMouseEnter={() => setIsDetailsHovered(true)}
          onMouseLeave={() => setIsDetailsHovered(false)}
        >
          View Profile
        </button>
        
        <Button
          size="sm"
          variant={isConnectionRequested ? "success" : "default"}
          onClick={(e) => {
            e.stopPropagation();
            onRequestConnection();
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
        >
          {isConnectionRequested ? (
            <>
              <Check style={{ width: '0.875rem', height: '0.875rem' }} />
              Connected
            </>
          ) : (
            <>
              <Zap style={{ width: '0.875rem', height: '0.875rem' }} />
              Connect
            </>
          )}
        </Button>
      </div>
    </div>
  );
});

const FirmCard = memo(({ firm, investorsWithFirms, dispatch, onSelectInvestor, type }) => {
  const { website, logo, firmName, investors } = firm;
  const status = (firm.status || 'active').toLowerCase();
  const firmHeaderBackground = status === 'inactive'
    ? 'linear-gradient(to right, #FEF2F2, #FFF5F5)'
    : status === 'follower'
    ? 'linear-gradient(to right, #FFFDE7, #FFFEF5)'
    : 'linear-gradient(to right, #f8fafc, white)';
  
  return (
    <Card style={{ overflow: 'hidden' }}>
      <div 
        style={{ ...styles.firmHeader, background: firmHeaderBackground }}
        onClick={() => website && window.open(website)}
      >
        <div style={styles.firmLogo}>
          {logo ? (
            <img src={logo} alt={firmName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Building2 style={{ width: '1.5rem', height: '1.5rem', color: '#94a3b8' }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={styles.firmName}>
            {firmName}
            {status !== 'active' && (
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                display: 'inline-block',
                marginLeft: '8px',
                verticalAlign: 'middle',
                backgroundColor: status === 'inactive' ? '#ef4444' : '#eab308',
              }} />
            )}
          </h2>
          <p style={styles.firmCount}>{investors?.length} {investors?.length === 1 ? 'person' : 'people'}</p>
        </div>
        {website && (
          <ExternalLink style={{ width: '1rem', height: '1rem', color: '#94a3b8' }} />
        )}
      </div>
      
      <div style={styles.investorsGrid}>
        {investors?.map((people) => (
          <InvestorCard
            key={people._id}
            people={people}
            firmName={firmName}
            isLiked={investorsWithFirms.liked.includes(people._id)}
            isConnectionRequested={investorsWithFirms.requestConnection.includes(people._id)}
            onViewDetails={() => onSelectInvestor({ ...people, firmName })}
            onLike={() => dispatch(setInvestorLiked({
              investorId: people._id,
              liked: investorsWithFirms.liked.includes(people._id)
            }))}
            onRequestConnection={() => dispatch(setInvestorRequestConnection({
              investorId: people._id
            }))}
          />
        ))}
      </div>
    </Card>
  );
});

const LoadingSkeleton = () => (
  <div style={styles.firmsList}>
    {[1, 2, 3].map((i) => (
      <Card key={i} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', borderBottom: '1px solid #f1f5f9' }}>
          <Skeleton style={{ width: '3rem', height: '3rem', borderRadius: '0.5rem' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Skeleton style={{ height: '1.25rem', width: '12rem' }} />
            <Skeleton style={{ height: '1rem', width: '6rem' }} />
          </div>
        </div>
        <div style={styles.investorsGrid}>
          {[1, 2, 3].map((j) => (
            <div key={j} style={{ padding: '1rem', borderRadius: '0.75rem', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <Skeleton style={{ width: '4rem', height: '4rem', borderRadius: '50%' }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Skeleton style={{ height: '1rem', width: '8rem' }} />
                  <Skeleton style={{ height: '0.75rem', width: '6rem' }} />
                  <Skeleton style={{ height: '0.75rem', width: '5rem' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <Skeleton style={{ height: '1.5rem', width: '4rem', borderRadius: '9999px' }} />
                <Skeleton style={{ height: '1.5rem', width: '5rem', borderRadius: '9999px' }} />
              </div>
            </div>
          ))}
        </div>
      </Card>
    ))}
  </div>
);

const DashboardType = () => {
  const { type } = useParams();
  const dispatch = useDispatch();
  const investorsWithFirms = useSelector((state) => state.investorsWithFirms);
  const filtersStore = useSelector((state) => state.filters);

  const activeTabKey = useMemo(() => resolveTabKey(type), [type]);
  const activeTab = useMemo(
    () => TABS.find((t) => t.key === activeTabKey) || TABS[0],
    [activeTabKey]
  );

  const [search_page, setsearch_page] = useState({});

  const [showing_filters_page, setshowing_filters_page] = useState({});

  const [current_filters_page, setcurrent_filters_page] = useState({});

  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [list, setlist] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [TotalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate per-tab filters and search from localStorage once on mount. Each
  // tab keeps its own namespace so switching tabs preserves its own state.
  useLayoutEffect(() => {
    let local_current_filters = localStorage.getItem("accel_filters_v2");
    local_current_filters = !local_current_filters
      ? null
      : JSON.parse(local_current_filters);
    if (local_current_filters && typeof local_current_filters === "object") {
      setcurrent_filters_page(local_current_filters);
    }

    let local_search = localStorage.getItem("accel_search_v2");
    local_search = !local_search ? null : JSON.parse(local_search);
    if (local_search && typeof local_search === "object") {
      setsearch_page(local_search);
    }
  }, []);

  const filteredData = useMemo(() => {
    if (!investorsWithFirms?.data?.length) return { list: [] };

    const cf = current_filters_page?.[activeTabKey] || {};
    const searchTxt = (search_page?.[activeTabKey] || "").toLowerCase();
    const tabType = activeTab.type;
    let new_list = [];

    investorsWithFirms.data.forEach((item) => {
      let filterd_investors = (item?.investors || []).filter((people) => {
        const peopleType = (people?.type || "").toLowerCase();
        let f_tab;
        if (tabType === "Others") {
          f_tab = !KNOWN_TYPES.some((t) => t.toLowerCase() === peopleType);
          if (f_tab && cf["Category"]?.length) {
            const sub = (people?.subCategory || "").toLowerCase();
            f_tab = cf["Category"].some((c) => c.toLowerCase() === sub);
          }
        } else {
          f_tab = peopleType === tabType.toLowerCase();
        }
        let f_fundingRound = cf["Funding Round"]?.length
          ? people?.stage?.some((ele) =>
              cf["Funding Round"].includes(ele.trim())
            )
          : true;
        let f_sector = cf["Sector"]?.length
          ? people?.sector?.some((ele) => cf["Sector"].includes(ele.trim()))
          : true;
        let f_city = cf["City"]?.length
          ? people?.locationCities?.some((ele) =>
              cf["City"].includes(ele.trim())
            )
          : true;
        let f_country = cf["Country"]?.length
          ? people?.locationCountries?.some((ele) =>
              cf["Country"].includes(ele.trim())
            )
          : true;
        let f_level = cf["Level"]?.length
          ? cf["Level"].includes(people?.level)
          : true;

        let f_search = searchTxt
          ? [
              people.name || "",
              people.locationCountries || "",
              people.designation || "",
              item.firmName,
            ]
              .join(", ")
              .toLowerCase()
              .includes(searchTxt)
          : true;

        return (
          f_tab &&
          f_fundingRound &&
          f_sector &&
          f_city &&
          f_country &&
          f_level &&
          f_search
        );
      });

      if (filterd_investors.length > 0) {
        new_list.push({ ...item, investors: filterd_investors });
      }
    });

    return { list: new_list };
  }, [investorsWithFirms?.data, current_filters_page, search_page, activeTabKey, activeTab]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const companiesPerPage = 10;
      let totalPages = Math.ceil(filteredData.list.length / companiesPerPage);
      const indexOfLastCompany = currentPage * companiesPerPage;
      const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
      const currentCompanies = filteredData.list.slice(indexOfFirstCompany, indexOfLastCompany);

      setlist(currentCompanies);
      setTotalPages(totalPages);
      setIsLoading(false);
    }, 50);
    
    return () => {
      clearTimeout(timer);
      setlist([]);
    };
  }, [filteredData, currentPage]);

  // Filter dropdown options are sourced from the managed `filters` collection
  // (via Redux) instead of being recomputed from every record on each load.
  useEffect(() => {
    const bc = filtersStore?.byCategory || {};
    const order = [
      "Funding Round",
      "Sector",
      "Country",
      "City",
    ];
    const built = order
      .filter((cat) => bc[cat]?.length)
      .map((cat) => ({
        title: cat,
        children: [...(bc[cat] || [])].sort((a, b) => a.localeCompare(b)),
      }));
    // Level is a fixed set (L1/L2/L3), not sourced from the managed filters.
    const withLevel = [...built, { title: "Level", children: [...LEVELS] }];
    // The active tab replaces the old "Type" filter. The Others tab adds a
    // dedicated sub-category filter (Advisors / Legal / Others).
    const withCategory =
      activeTab.type === "Others"
        ? [{ title: "Category", children: [...OTHERS_SUBCATEGORIES] }, ...withLevel]
        : withLevel;
    setshowing_filters_page((prev) => ({ ...prev, [activeTabKey]: withCategory }));
  }, [filtersStore?.byCategory, activeTabKey, activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [current_filters_page, search_page, activeTabKey]);

  const handleNextPage = useCallback(() => {
    if (currentPage < TotalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, TotalPages]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handleSearch = useCallback((txt) => {
    setsearch_page((prev) => {
      let obj = { ...prev, [activeTabKey]: txt };
      localStorage.setItem("accel_search_v2", JSON.stringify(obj));
      return obj;
    });
  }, [activeTabKey]);

  const handleClearFilters = useCallback(() => {
    setcurrent_filters_page((prev) => {
      let obj = { ...prev, [activeTabKey]: { ...filters } };
      localStorage.setItem("accel_filters_v2", JSON.stringify(obj));
      return obj;
    });
  }, [activeTabKey]);

  const handleSetFilter = useCallback((key, value) => {
    setcurrent_filters_page((prev) => {
      const cur = prev[activeTabKey] || {};
      let obj = {
        ...prev,
        [activeTabKey]: {
          ...cur,
          [key]: cur?.[key]?.includes(value)
            ? cur[key].filter((el) => el !== value)
            : [...(cur[key] || []), value],
        },
      };
      localStorage.setItem("accel_filters_v2", JSON.stringify(obj));
      return obj;
    });
  }, [activeTabKey]);

  return (
    <div style={styles.pageContainer}>
      <div style={styles.stickyHeader}>
        <div style={styles.filterWrapper}>
          <div className="s_f_section" style={styles.filterSection}>
          <div style={styles.filterRow}>
            <div style={{ flex: 1 }}>
              <SearchAndFilter
                {...{
                  replacetxt: {},
                  count: filteredData.list?.length || 0,
                  search: search_page[activeTabKey] || "",
                  setsearch: handleSearch,
                  showing_filters: showing_filters_page?.[activeTabKey] || [],
                  current_filters: current_filters_page?.[activeTabKey] || {},
                  clear_filters: handleClearFilters,
                  setcurrent_filters: handleSetFilter,
                  filter_modal: true,
                }}
              />
            </div>
            
            {TotalPages > 1 && (
              <div style={styles.paginationWrapper}>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.paginationButton,
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ChevronLeft style={{ width: '1.25rem', height: '1.25rem' }} />
                </button>
                <span style={styles.paginationText}>
                  {currentPage} of {TotalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === TotalPages}
                  style={{
                    ...styles.paginationButton,
                    opacity: currentPage === TotalPages ? 0.4 : 1,
                    cursor: currentPage === TotalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ChevronRight style={{ width: '1.25rem', height: '1.25rem' }} />
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        <div className="main_body" style={styles.firmsList}>
          {isLoading && !list.length ? (
            <LoadingSkeleton />
          ) : !list?.length ? (
            <Card style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                <Building2 style={{ width: '4rem', height: '4rem', margin: '0 auto' }} />
              </div>
              <h3 style={styles.emptyTitle}>No results found</h3>
              <p style={styles.emptyText}>
                Try adjusting your search or filters to find what you're looking for.
              </p>
              <Button variant="outline" onClick={handleClearFilters} style={{ marginTop: '1rem' }}>
                Clear all filters
              </Button>
            </Card>
          ) : (
            list.map((firm, index) => (
              <FirmCard
                key={firm._id || index}
                firm={firm}
                investorsWithFirms={investorsWithFirms}
                dispatch={dispatch}
                onSelectInvestor={setSelectedInvestor}
                type={type}
              />
            ))
          )}
        </div>

        {TotalPages > 1 && list.length > 0 && (
          <div style={styles.bottomPagination}>
            <div style={styles.bottomPaginationInner}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
                Previous
              </Button>
              <span style={styles.bottomPaginationText}>
                Page {currentPage} of {TotalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === TotalPages}
                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                Next
                <ChevronRight style={{ width: '1rem', height: '1rem' }} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <InvestorModal
        investor={selectedInvestor}
        firmName={selectedInvestor?.firmName}
        isOpen={!!selectedInvestor}
        onClose={() => setSelectedInvestor(null)}
        isLiked={investorsWithFirms.liked.includes(selectedInvestor?._id)}
        isConnectionRequested={investorsWithFirms.requestConnection.includes(selectedInvestor?._id)}
        onLike={() => {
          if (selectedInvestor) {
            dispatch(setInvestorLiked({
              investorId: selectedInvestor._id,
              liked: investorsWithFirms.liked.includes(selectedInvestor._id)
            }));
          }
        }}
        onRequestConnection={() => {
          if (selectedInvestor) {
            dispatch(setInvestorRequestConnection({
              investorId: selectedInvestor._id
            }));
          }
        }}
      />
    </div>
  );
};

export default DashboardType;
